/*
 * PDP Read More Modal — open / close + slider navigation.
 *
 * Stage 1 wired the open / close half: clicking a
 * `[data-readmore-trigger]` button opens the modal and jumps to
 * the matching slide.
 *
 * Stage 2 adds the slider half:
 *   - Prev / Next buttons cycle through all 5 slides in DOM order.
 *   - At the edges the slider LOOPS — Prev on the first slide jumps
 *     to the last, Next on the last jumps to the first.
 *   - The Prev / Next button labels show the title of the DESTINATION
 *     slide (e.g. "← Reviews" / "Size & Fit →"), so the shopper can
 *     see which modal they'll switch to before clicking.
 *   - Counter shows "1 / 5", "2 / 5", etc.
 *   - Slide changes fade via CSS `opacity` transitions (each slide
 *     stacked in the same Grid cell — see component CSS).
 *
 * Slide visibility is class-based, not the `hidden` attribute, so
 * the CSS opacity transitions can actually run. All slides stay in
 * the DOM at all times; only `.is-active` swaps.
 */

const MODAL_SELECTOR = '[data-pdp-readmore-modal]';
const CONTAINER_SELECTOR = '.pdp-readmore-modal__container';
const SLIDE_SELECTOR = '[data-readmore-slide]';
const TRIGGER_SELECTOR = '[data-readmore-trigger]';
const TITLE_SELECTOR = '[data-readmore-title]';
const PREV_SELECTOR = '[data-readmore-prev]';
const NEXT_SELECTOR = '[data-readmore-next]';
const PREV_LABEL_SELECTOR = '[data-readmore-prev-label]';
const NEXT_LABEL_SELECTOR = '[data-readmore-next-label]';
const COUNTER_SELECTOR = '[data-readmore-counter]';
const BODY_SELECTOR = '[data-readmore-body]';
const FADE_TOP_SELECTOR = '[data-readmore-scroll-fade-top]';
const FADE_BOTTOM_SELECTOR = '[data-readmore-scroll-fade-bottom]';
const VISIBLE_CLASS = 'is-visible';
const ACTIVE_CLASS = 'is-active';
const SHOW_CLASS = 'pdp-readmore-modal--show';

class PdpReadmoreModal {
  constructor(root) {
    this.root = root;
    this.container = root.querySelector(CONTAINER_SELECTOR);
    this.slides = Array.from(root.querySelectorAll(SLIDE_SELECTOR));
    this.titleEl = root.querySelector(TITLE_SELECTOR);
    this.prevBtn = root.querySelector(PREV_SELECTOR);
    this.nextBtn = root.querySelector(NEXT_SELECTOR);
    this.prevLabelEl = root.querySelector(PREV_LABEL_SELECTOR);
    this.nextLabelEl = root.querySelector(NEXT_LABEL_SELECTOR);
    this.counterEl = root.querySelector(COUNTER_SELECTOR);
    this.bodyEl = root.querySelector(BODY_SELECTOR);
    this.fadeTopEl = root.querySelector(FADE_TOP_SELECTOR);
    this.fadeBottomEl = root.querySelector(FADE_BOTTOM_SELECTOR);
    this.activeIndex = 0;
    this.abort = new AbortController();

    if (!this.container || this.slides.length === 0) return;

    // Strip the initial `hidden` from the container so it can
    // animate in once a trigger fires.
    this.container.hidden = false;

    this._bindTriggers();
    this._bindClose();
    this._bindEscape();
    this._bindNav();
    this._bindBodyScroll();

    // Default to the first slide visually so opening via a missing
    // / unknown key still has something on screen. The open() call
    // will swap to the correct one before the panel slides in.
    this._setActive(0, { skipFade: true });
  }

  _bindTriggers() {
    document.querySelectorAll(TRIGGER_SELECTOR).forEach((btn) => {
      btn.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          const key = btn.dataset.readmoreKey;
          if (key) this.open(key);
        },
        { signal: this.abort.signal }
      );
    });
  }

  _bindClose() {
    this.root.querySelectorAll('[data-readmore-close]').forEach((el) => {
      el.addEventListener('click', () => this.close(), {
        signal: this.abort.signal,
      });
    });
  }

  _bindEscape() {
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && document.body.classList.contains(SHOW_CLASS)) {
          this.close();
        }
      },
      { signal: this.abort.signal }
    );
  }

  _bindNav() {
    this.prevBtn?.addEventListener('click', () => this._step(-1), {
      signal: this.abort.signal,
    });
    this.nextBtn?.addEventListener('click', () => this._step(1), {
      signal: this.abort.signal,
    });
  }

  /*
   * Scroll-fade overlays — show only when there's content to
   * scroll towards in that direction. Bound to the body's scroll
   * event so the toggle is live as the shopper drags. Initial
   * state is set by _updateScrollFades() — called after every
   * slide swap and on open(), because both can change which
   * fades should be visible.
   */
  _bindBodyScroll() {
    if (!this.bodyEl) return;
    this.bodyEl.addEventListener(
      'scroll',
      () => this._updateScrollFades(),
      { signal: this.abort.signal, passive: true }
    );
  }

  _updateScrollFades() {
    if (!this.bodyEl) return;
    const { scrollTop, scrollHeight, clientHeight } = this.bodyEl;
    // 1px tolerance — Safari can leave scrollTop at e.g. 0.5 after
    // a swap; treating that as "at the top" prevents the top fade
    // from flashing on briefly. Same idea for the bottom edge.
    const canScrollUp = scrollTop > 1;
    const canScrollDown = scrollTop + clientHeight < scrollHeight - 1;
    this.fadeTopEl?.classList.toggle(VISIBLE_CLASS, canScrollUp);
    this.fadeBottomEl?.classList.toggle(VISIBLE_CLASS, canScrollDown);
  }

  open(key) {
    const index = this.slides.findIndex((s) => s.dataset.readmoreKey === key);
    if (index === -1) return;

    this._setActive(index);
    document.body.classList.add(SHOW_CLASS);

    // Focus the close button by default — keyboard-friendly entry
    // that doesn't trap users inside the slide's content.
    requestAnimationFrame(() => {
      this.root.querySelector('[data-readmore-close]')?.focus();
      // Re-check scroll fades after the open transition has had
      // a frame to settle layout — `clientHeight` reads 0 while
      // the panel is still off-screen on some browsers.
      this._updateScrollFades();
    });
  }

  close() {
    document.body.classList.remove(SHOW_CLASS);
    // Leave the active slide visible — hiding it now would cause
    // a layout-shift during the closing transition. The next open()
    // call will swap to the correct slide.
  }

  /**
   * Move active slide by `delta` (+1 next, -1 prev). Loops at the
   * edges so this is a circular slider.
   */
  _step(delta) {
    const count = this.slides.length;
    if (count === 0) return;
    const nextIndex = (this.activeIndex + delta + count) % count;
    this._setActive(nextIndex);
  }

  _setActive(index, { skipFade = false } = {}) {
    const count = this.slides.length;
    if (count === 0) return;
    if (index < 0 || index >= count) return;

    this.activeIndex = index;
    const active = this.slides[index];

    if (skipFade) {
      // Initial paint — apply state immediately, no transition.
      // Suppress the CSS transition by toggling a no-transition
      // class for one frame.
      this.slides.forEach((s) => {
        s.classList.toggle(ACTIVE_CLASS, s === active);
      });
    } else {
      this.slides.forEach((s) => {
        s.classList.toggle(ACTIVE_CLASS, s === active);
      });
    }

    // Reset scroll to top on every slide change. The modal body is
    // a single scroll container shared across slides (each slide's
    // scrollHeight drives overflow independently), so switching
    // slides without a reset leaves the reader at whatever scroll
    // offset the previous slide had — which lands mid-paragraph in
    // the new slide. Applied on the initial-paint path (skipFade)
    // too so freshly-opened modals always start at the top.
    if (this.bodyEl) this.bodyEl.scrollTop = 0;

    this._updateHeader(active);
    this._updateNavLabels();
    this._updateCounter();
    // Scroll-fade visibility depends on the active slide's height
    // (body's scrollHeight tracks the tallest slide; the visible
    // slide is the one driving overflow). Recompute after the
    // active slide swap so the fades match the new content.
    this._updateScrollFades();
  }

  _updateHeader(slide) {
    if (!this.titleEl) return;
    this.titleEl.textContent = slide.dataset.readmoreTitle || '';
  }

  _updateNavLabels() {
    // Visible button text uses the SHORT nav label
    // (data-readmore-nav-label, e.g. "Returns"). aria-labels use
    // the LONG title (data-readmore-title, e.g. "Returns,
    // Exchanges & Refunds") so screen readers still announce the
    // full destination name. Keeping the visible label short
    // prevents long destination titles from pushing one button's
    // grid track past the symmetric 1fr 1fr footer layout.
    const count = this.slides.length;
    if (count === 0) return;

    const prevIndex = (this.activeIndex - 1 + count) % count;
    const nextIndex = (this.activeIndex + 1) % count;
    const prevSlide = this.slides[prevIndex];
    const nextSlide = this.slides[nextIndex];
    const prevLabel = prevSlide.dataset.readmoreNavLabel || prevSlide.dataset.readmoreTitle || '';
    const nextLabel = nextSlide.dataset.readmoreNavLabel || nextSlide.dataset.readmoreTitle || '';
    const prevTitle = prevSlide.dataset.readmoreTitle || prevLabel;
    const nextTitle = nextSlide.dataset.readmoreTitle || nextLabel;

    if (this.prevLabelEl) this.prevLabelEl.textContent = prevLabel;
    if (this.nextLabelEl) this.nextLabelEl.textContent = nextLabel;

    if (this.prevBtn) this.prevBtn.setAttribute('aria-label', `Previous: ${prevTitle}`);
    if (this.nextBtn) this.nextBtn.setAttribute('aria-label', `Next: ${nextTitle}`);
  }

  _updateCounter() {
    if (!this.counterEl) return;
    this.counterEl.textContent = `${this.activeIndex + 1} / ${this.slides.length}`;
  }

  destroy() {
    this.abort.abort();
  }
}

function init() {
  document.querySelectorAll(MODAL_SELECTOR).forEach((root) => {
    if (root._pdpReadmoreInstance) return;
    root._pdpReadmoreInstance = new PdpReadmoreModal(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    event.target.querySelectorAll(MODAL_SELECTOR).forEach((root) => {
      if (root._pdpReadmoreInstance) {
        root._pdpReadmoreInstance.destroy();
        delete root._pdpReadmoreInstance;
      }
      root._pdpReadmoreInstance = new PdpReadmoreModal(root);
    });
  }
});
